import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, tap } from 'rxjs/operators';
import { Journal } from '../domain/journal';
import { BaseService } from './base-service';
import moment from 'moment';

@Injectable({ providedIn: 'root' })
export class JournalService extends BaseService {
  private journalUrl = environment.apiUrl + 'journal'; // URL to web api

  private journalCache: Map<String, Observable<Journal>> = new Map();

  getJournalDates(): Observable<Journal[]> {
    let httpOptions = this.buildHeaders();
    return this.http
      .get<Journal[]>(this.journalUrl + '/dates', httpOptions)
      .pipe(
        tap((_) => this.clearStatus()),
        catchError(this.handleError<Journal[]>('getJournalDates', [])),
      );
  }

  getJournal(date: Date): Observable<Journal> {
    if (this.journalCache.has(date.toDateString())) {
      return this.journalCache.get(date.toDateString());
    }
    let httpOptions = this.buildHeaders();
    httpOptions.params = new HttpParams().append('dateLong', date.getTime());
    const url = `${this.journalUrl}`;
    const journal$ = this.http.get<Journal>(url, httpOptions).pipe(
      tap((_) => this.clearStatus()),
      catchError(this.handleError<Journal>(`getTJournal id=${date}`)),
      shareReplay(1),
    );
    this.journalCache.set(date.toDateString(), journal$);
    return journal$;
  }

  refreshJournals(): void {
    this.journalCache = new Map();
  }

  getJournals(): Observable<Journal[]> {
    let httpOptions = this.buildHeaders();
    return this.http.get<Journal[]>(this.journalUrl + '/all', httpOptions).pipe(
      tap((_) => this.clearStatus()),
      catchError(this.handleError<Journal[]>('getJournalAll', [])),
    );
  }

  getJournalsByText(text: string): Observable<Journal[]> {
    let httpOptions = this.buildHeaders();
    httpOptions.params = new HttpParams().append('text', text);
    return this.http
      .get<Journal[]>(this.journalUrl + '/search', httpOptions)
      .pipe(
        tap((_) => this.clearStatus()),
        catchError(this.handleError<Journal[]>('getJournalAll', [])),
      );
  }

  getJournalsByTextAndDate(text: string, date: Date): Observable<Journal[]> {
    const fromDate = new Date(date); // make a copy
    fromDate.setDate(date.getDate() - 30);
    let httpOptions = this.buildHeaders();
    httpOptions.params = new HttpParams()
      .append('text', text)
      .append('fromDateLong', fromDate.getTime())
      .append('toDateLong', date.getTime());

    return this.http
      .get<Journal[]>(this.journalUrl + '/searchdates', httpOptions)
      .pipe(
        tap((_) => this.clearStatus()),
        catchError(this.handleError<Journal[]>('getJournalAll', [])),
      );
  }

  updateJournal(journal: Journal): Observable<any> {
    this.refreshJournals();
    if (journal.id > 0 && journal.entry.trim().length == 0) {
      this.deleteJournal(journal.id).subscribe();
    }
    let httpOptions = this.buildHeaders();
    return this.http
      .put(this.journalUrl + '/' + journal.id, journal, httpOptions)
      .pipe(
        tap((_) => this.setStatus('Updated journal')),
        catchError(this.handleError<any>('updateJournal')),
      );
  }

  addJournal(journal: Journal): Observable<any> {
    this.refreshJournals();
    if (journal.entry.trim().length == 0) {
      return null;
    }
    let httpOptions = this.buildHeaders();
    return this.http.post(this.journalUrl, journal, httpOptions).pipe(
      tap((_) => this.setStatus('Updated journal')),
      catchError(this.handleError<any>('updateJournal')),
    );
  }

  deleteJournal(id: number): Observable<Journal> {
    this.refreshJournals();
    let httpOptions = this.buildHeaders();
    const url = `${this.journalUrl}/${id}`;
    return this.http.delete<Journal>(url, httpOptions).pipe(
      tap((_) => this.setStatus(`deleted journal id=${id}`)),
      catchError(this.handleError<Journal>('deleteJournal')),
    );
  }
}
